/* eslint-disable i18next/no-literal-string */
export type ScreenHeight = 'short' | 'tall';
export type ScreenMode = 'landscape' | 'portrait';

/**
 * Responsive breakpoints (width)
 */
export enum ScreenSize {
  xxSmall = 360,
  xSmall = 480,
  small = 640,
  medium = 1024,
  large = 1366,
  xLarge = 1920,
  xxLarge = Number.POSITIVE_INFINITY,
}

/**
 * Windows & macOS screen webview dialogs are 768x560
 */
export const webviewDialogWidth = 768;
export const webviewDialogHeight = 560;
/**
 * Short screen height
 */
export const shortHeight = 360;

/**
 * List of {@link ScreenSize}
 */
export const screenSizes = Object.values(ScreenSize).filter(Number) as ScreenSize[];

export function isTinyScreen(screenSize: ScreenSize): boolean {
  return screenSize <= ScreenSize.xxSmall;
}

export function isMobileScreen(screenSize: ScreenSize): boolean {
  return screenSize <= ScreenSize.small;
}

export function isSmallScreen(screenSize: ScreenSize): boolean {
  return screenSize <= ScreenSize.medium;
}

export function isLargeScreen(screenSize: ScreenSize): boolean {
  return screenSize > ScreenSize.medium;
}

export function isXLargeScreen(screenSize: ScreenSize): boolean {
  return screenSize > ScreenSize.large;
}

export function isShortScreen(screenHeight: ScreenHeight): boolean {
  return ['short'].includes(screenHeight);
}

export function getScreenSize(): ScreenSize {
  const { innerWidth } = window;
  return (
    screenSizes.find((screenSize) => innerWidth <= screenSize) ??
    // the coalesce should not happen since xxLarge is infinity
    ScreenSize.xxLarge
  );
}

export function getScreenMode(): ScreenMode {
  if (window.innerHeight > window.innerWidth) return 'portrait';
  return 'landscape';
}

export function getScreenHeight(): ScreenHeight {
  if (window.innerHeight < shortHeight) return 'short';
  return 'tall';
}

export function pixelToViewportWidth(width: number, screen: ScreenSize): string {
  let widthString = `${width}px`;

  switch (screen) {
    case ScreenSize.xxSmall:
      widthString = `${(width / ScreenSize.xxSmall) * 100}vw`;
      break;
    case ScreenSize.xSmall:
      widthString = `${(width / ScreenSize.xxSmall) * 100}vw`;
      break;
    case ScreenSize.small:
      widthString = `${(width / ScreenSize.xSmall) * 100}vw`;
      break;
    case ScreenSize.medium:
      widthString = `${(width / ScreenSize.small) * 100}vw`;
      break;
    case ScreenSize.large:
      widthString = `${(width / ScreenSize.medium) * 100}vw`;
      break;
    case ScreenSize.xLarge:
      widthString = `${(width / ScreenSize.large) * 100}vw`;
      break;
    case ScreenSize.xxLarge:
      widthString = `${(width / ScreenSize.xLarge) * 100}vw`;
      break;
    default:
      break;
  }
  return widthString;
}

export function pixelToViewportHeight(height: number, screen: ScreenSize): string {
  let heightString = `${height}px`;

  switch (screen) {
    case ScreenSize.xxSmall:
    case ScreenSize.xSmall:
    case ScreenSize.small:
    case ScreenSize.medium:
    case ScreenSize.large:
      heightString = `${(height / 768) * 100}vH`;
      break;
    case ScreenSize.xLarge:
    case ScreenSize.xxLarge:
      heightString = `${(height / 1080) * 100}vH`;
      break;
    default:
      break;
  }

  return heightString;
}
